import { useState, useCallback, useEffect } from 'react';
import { EventData, EVENTS, STATUS, Step } from 'react-joyride';

const TOUR_KEY = 'solimat-home-tour-v1';

export const useHomeTour = () => {
  const [run, setRun] = useState(false);
  const [tourKey, setTourKey] = useState(0);

  const steps: Step[] = [
      // ── Navegación principal ──────────────────────────────────────────
   /*  {
      target: '.nav-new-container',
      title: 'Menú de navegación',
      content: 'Desde aquí accedes a todas las secciones de tu área de paciente. Cada enlace te lleva a un área específica de gestión y seguimiento.',
      placement: 'bottom',
      skipBeacon: true,
    }, */
    {
      target: 'a.nav-new_link[href="/inicio"]',
      title: 'Inicio',
      content: 'Tu panel principal. Consulta tu próxima cita, accede a la red de centros, comunícate con tu gestor y descubre el esquema general de tu prestación de un vistazo.',
      placement: 'bottom',
      skipBeacon: true,
    },
    {
      target: 'a.nav-new_link[href="/unidad-delta"]',
      title: 'Unidad Delt@',
      content: ' Aquí puedes gestionar los accidentes con baja y sin baja pendientes de tramitar ante la autoridad laboral.(Sistema Delt@)',
      placement: 'bottom',
      skipBeacon: true,
    },
    {
      target: 'a.nav-new_link[href="/mis-citas"]',
      title: 'Mis citas',
      content: 'Consulta todas tus citas médicas programadas: fecha, hora, especialidad y centro asistencial. Puedes ver el detalle de cada cita y el historial de visitas anteriores.',
      placement: 'bottom',
      skipBeacon: true,
    },
    {
      target: 'a.nav-new_link[href="/mis-gestiones"]',
      title: 'Mis gestiones',
      content: 'Accede a todos tus trámites activos: formularios pendientes, solicitudes en curso y gestiones administrativas relacionadas con tu prestación.',
      placement: 'bottom',
      skipBeacon: true,
    },
    {
      target: 'a.nav-new_link[href="/historial"]',
      title: 'Historial',
      content: 'Consulta el historial completo de tus prestaciones. Desde aquí puedes acceder a la información asistencial (consultas, rehabilitación, quirófano…), documentación y comunicaciones de cada proceso.',
      placement: 'bottom',
      skipBeacon: true,
    },
    {
      target: 'a.nav-new_link[href="/notificaciones"]',
      title: 'Notificaciones',
      content: 'Recibe y gestiona todos los avisos importantes de Solimat: cambios en tus prestaciones, documentos disponibles, citas confirmadas y otras comunicaciones relevantes.',
      placement: 'bottom',
      skipBeacon: true,
    },
    {
      target: 'a.nav-new_link[href="/comunicaciones"]',
      title: 'Comunicaciones',
      content: 'Consulta y responde los mensajes de tu gestor. Aquí encontrarás el historial completo de comunicaciones asociadas a tus prestaciones.',
      placement: 'bottom',
      skipBeacon: true,
    },
    // ── Contenido de la página de inicio ─────────────────────────────
    {
      target: '.hero',
      title: 'Bienvenido a tu área de paciente',
      content: 'Este es tu panel principal. Aquí encontrarás un resumen de tu actividad y el acceso a tu próxima cita.',
      placement: 'bottom',
      skipBeacon: true,
    },
    {
      target: 'button.hero-btn--outline',
      title: 'Contactar gestor',
      content: 'Con este botón puedes enviar un mensaje directo a tu gestor asignado. Úsalo para resolver dudas o gestionar cualquier incidencia relacionada con tu prestación.',
      placement: 'right',
      skipBeacon: true,
    },
    {
      target: '.hero__appt',
      title: 'Tu próxima cita',
      content: 'Consulta aquí la fecha, hora y centro de tu próxima cita médica programada. Haz clic para ver todos los detalles.',
      placement: 'auto',
      skipBeacon: true,
    },
 /*    {
      target: '.alerts-section',
      title: 'Avisos importantes',
      content: 'Aquí aparecen los avisos que requieren tu atención: documentos pendientes, formularios por cumplimentar y otros comunicados de Solimat que no debes perder de vista.',
      placement: 'top',
      skipBeacon: true,
    }, */
    {
      target: '.qa-section',
      title: 'Acciones rápidas',
      content: 'Accede de un clic a las secciones que más utilizas: historial de prestaciones, citas, comunicaciones y gestiones.',
      placement: 'top',
      skipBeacon: true,
    },
    {
      target: '.trust',
      title: 'Solimat Mutua',
      content: 'Conoce más sobre Solimat Mutua y la atención integral que pone a tu disposición como mutualista.',
      placement: 'top',
      skipBeacon: true,
    },
    {
      target: 'section.section',
      title: 'Red de centros',
      content: 'Explora la red de centros asistenciales de Solimat y encuentra el más cercano a ti.',
      placement: 'top',
      skipBeacon: true,
    },
    {
      target: '.appdl',
      title: 'App móvil de Solimat',
      content: 'Gestiona tu prestación desde cualquier lugar. Descarga la aplicación móvil disponible para iOS y Android.',
      placement: 'top',
      skipBeacon: true,
    },
  ];

  const handleCallback = useCallback((data: EventData) => {
    const { type, status, index } = data as any;
    const finished = status === STATUS.FINISHED || status === STATUS.SKIPPED;

    if (finished || (type as string) === EVENTS.TOUR_END) {
      setRun(false);
      localStorage.setItem(TOUR_KEY, 'true');
      window.dispatchEvent(new CustomEvent('solimat:tourStateChange', { detail: { active: false } }));
    }

    // Ocultar label+pin al avanzar más allá del paso 0 (beacon pulsado)
    if ((type as string) === EVENTS.STEP_AFTER && index === 0) {
      window.dispatchEvent(new CustomEvent('solimat:tourStateChange', { detail: { active: false } }));
    }
  }, []);

  const startTour = useCallback(() => {
    setRun(current => {
      if (current) {
        return false;
      }
      setTourKey(k => k + 1);
      return true;
    });
  }, []);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('solimat:tourStateChange', { detail: { active: run } }));
  }, [run]);

  return { run, tourKey, steps, handleCallback, startTour };
};
